import uniq from "lodash/uniq";
import React, { useMemo } from "react";
import { InfoIcon, Threedot } from "@novel/shared/icons";
import { Tooltip, Whisper } from "rsuite";
import HandWaveIcon from "@novel/shared/icons/HandWaveIcon.png";
import Image from "next/legacy/image";
import { FadeInOnLoadMedia } from "@novel/shared/components/FadeInOnLoadMedia";
import { RadioButton } from "@shopify/polaris";
import { Box } from "@novel/shared/components/Box";
import NovelLoader from "@novel/shared/components/NovelLoader";
interface ITableCellImagesListProps {
  readonly imageUrls: string[];
  readonly displayMax?: number;
  readonly groupDupes?: boolean;
  readonly size?: "SMALL" | "BIG" | "MEDIUM";
  readonly singleSelect?: boolean;
}
interface ITextWithProductImagesList {
  readonly imageUrls: string[] | string | undefined;
  readonly text?: string;
  readonly size?: "SMALL" | "BIG" | "MEDIUM";
  readonly reverse?: boolean;
  readonly subText?: string;
  readonly singleSelect?: boolean;
  readonly tooltipText?: string;
  readonly selected?: boolean;
  readonly showRadio?: boolean;
  readonly disabled?: boolean;
  readonly syncing?: boolean;
  readonly syncingElement?: JSX.Element;
}
export function TextWithProductImagesList({
  text,
  imageUrls,
  subText,
  size,
  reverse,
  singleSelect,
  tooltipText,
  selected,
  showRadio,
  disabled,
  syncing,
  syncingElement
}: ITextWithProductImagesList): JSX.Element {
  const imageUrlsArray = useMemo(() => (Array.isArray(imageUrls) ? imageUrls : [imageUrls]).filter((imageUrl): imageUrl is string => !!imageUrl), [imageUrls]);
  const style = useMemo(() => imageUrlsArray.length && !reverse ? {
    marginRight: 10,
    marginLeft: 10
  } : {}, [imageUrlsArray.length, reverse]);
  return <React.Fragment>
            {reverse ? <div className="flex items-center justify-between w-full">
                    <div className="text-box-to-work">
                        {text ? <div style={style} className="line-height-21">
                                {text}
                            </div> : null}
                        {subText ? <div style={style} className="small-size-px">
                                {subText}
                            </div> : null}
                    </div>
                    {imageUrlsArray.length ? <TableCellImagesList imageUrls={imageUrlsArray} size={size} singleSelect={singleSelect} /> : null}
                    {tooltipText ? <Whisper placement="autoVerticalStart" controlId="control-id-hover" trigger={["hover", "click"]} speaker={<Tooltip className="tooltip-flexible-lg">
                                    <div className="flex justify-center">
                                        <span className="emoji-tooltip-design">
                                            <Image src={HandWaveIcon} alt="HandWaveIcon" layout="fixed" />{" "}
                                        </span>
                                        <span className="text-tooltip-design">{tooltipText}</span>
                                    </div>
                                </Tooltip>}>
                            <span>
                                <InfoIcon />
                            </span>
                        </Whisper> : null}
                </div> : <div className="flex justify-between items-center full-width">
                    <div className="flex items-center">
                        {tooltipText ? <Whisper placement="autoVerticalStart" controlId="control-id-hover" trigger={["hover", "click"]} speaker={<Tooltip className="tooltip-flexible-lg">
                                        <div className="flex justify-center">
                                            <span className="emoji-tooltip-design">
                                                <Image src={HandWaveIcon} alt="HandWaveIcon" layout="fixed" />{" "}
                                            </span>
                                            <span className="text-tooltip-design">
                                                {tooltipText}
                                            </span>
                                        </div>
                                    </Tooltip>}>
                                <span>
                                    <InfoIcon />
                                </span>
                            </Whisper> : null}
                        {showRadio && <Box mr={1} ml={2} onClick={e => e.preventDefault()}>
                                <RadioButton label="" disabled={disabled} checked={selected || false} id="disabled" />
                            </Box>}
                        {imageUrlsArray.length ? <TableCellImagesList imageUrls={imageUrlsArray} size={size} singleSelect={singleSelect} /> : null}
                        <div className="text-box-to-work">
                            {text ? <div style={style} className="line-height-21">
                                    {text}
                                </div> : null}
                            {subText ? <div style={style} className="small-size-px">
                                    {subText}
                                </div> : null}
                        </div>
                    </div>
                    {syncing && (syncingElement || <NovelLoader speed="fast" />)}
                </div>}
            <style global jsx>{`
                .text-box-to-work {
                    display: flex;
                    flex-direction: column;
                }
                .full-width {
                    width: 100%;
                }
                .line-height-21 {
                    line-height: 21px;
                }
                .small-size-px {
                    font-size: 11px;
                    font-weight: 400;
                    color: #959595;
                }
                .Polaris-OptionList-Option__Label,
                .Polaris-OptionList-Option__SingleSelectOption {
                    height: 100%;
                    align-items: center;
                }
            `}</style>
        </React.Fragment>;
}
export function TableCellImagesList({
  imageUrls,
  displayMax,
  size,
  groupDupes = true,
  singleSelect
}: ITableCellImagesListProps): JSX.Element {
  const cleanedImages = useMemo(() => imageUrls.filter(v => typeof v === "string"),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [imageUrls.slice().sort().join("|")]);
  const uniqueImages = useMemo(() => uniq(cleanedImages).slice().sort(), [cleanedImages]);
  const imageCounts = useMemo(() => cleanedImages.reduce((accum: {
    [key: string]: number;
  }, curr: string) => {
    if (accum[curr]) {
      accum[curr]++;
    } else {
      accum[curr] = 1;
    }
    return accum;
  }, {}),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [imageUrls]);
  const finalChosenArray = groupDupes ? uniqueImages : cleanedImages;
  const finalArray = useMemo(() => !displayMax ? finalChosenArray : finalChosenArray.slice(0, displayMax), [displayMax, finalChosenArray]);
  const extraItems = !displayMax ? 0 : Math.max(finalChosenArray.length - displayMax, 0);
  return <div className="table-cell-images-list">
            {finalArray.map((imageUrl, index) => <div className={`${size ? size === "BIG" ? "table-cell-image-container" : size === "MEDIUM" ? "table-cell-image-container-md" : "table-cell-image-container-sm" : "table-cell-image-container"}`} key={index}>
                    <FadeInOnLoadMedia className="table-cell-image" src={imageUrl} />
                    {groupDupes && imageCounts[imageUrl] && imageCounts[imageUrl] > 1 && <div className="table-cell-image-count">{imageCounts[imageUrl]}</div>}
                </div>)}
            {extraItems > 0 && <div className={`${size ? size === "BIG" ? "table-cell-image-container" : "table-cell-image-container-sm" : "table-cell-image-container"} table-cell-counter`}>
                    <Threedot />
                </div>}
            <style global jsx>{`
                .table-cell-images-list {
                    position: relative;
                    height: 100%;
                    justify-content: center;
                    align-items: center;
                    display: flex;
                }

                .table-cell-image-container-sm {
                    position: relative;
                    margin-right: 10px;
                    height: 36px;
                    width: 36px;
                    border-radius: 3px;
                    box-shadow: 1px 1px 2.5px rgba(0, 0, 0, 0.3);
                }

                .table-cell-image-container-md {
                    position: relative;
                    margin-right: 10px;
                    height: 48px;
                    width: 48px;
                    border-radius: 3px;
                    box-shadow: 1px 1px 2.5px rgba(0, 0, 0, 0.3);
                }

                .table-cell-image-container {
                    position: relative;
                    margin-right: 10px;
                    height: 68px;
                    width: 68px;
                    border-radius: 3px;
                    box-shadow: 1px 1px 2.5px rgba(0, 0, 0, 0.3);
                }

                .table-cell-counter {
                    display: flex;
                    background-color: #f7f7f7;
                    min-width: 38px;
                    padding: 6px;
                    align-items: center;
                    color: #c4c4c4;
                    justify-content: center;
                    font-size: 18px;
                    // box-shadow: 1px 1px 2.5px rgb(0 0 0 / 30%);
                }

                .table-cell-image {
                    position: relative;
                    height: 100%;
                    border-radius: 4px;
                    object-fit: cover;
                    overflow: hidden;
                    width: 100%;
                }

                .table-cell-image-count {
                    position: absolute;
                    top: 0;
                    right: 0;
                    transform: translate(40%, -40%);
                    color: rgba(0, 0, 0, 0.3);
                    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.6);
                    background-color: #3193dc;
                    border-radius: 50%;
                    height: 14px;
                    width: 14px;
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 600;
                    font-size: 10px;
                }
            `}</style>
        </div>;
}